import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { KpiCard } from "@/components/dashboard/widgets";
import { MailOpen, Sparkles, CheckCircle2, Inbox as InboxIcon, Pause, Play, Trash2, MessageCircleReply } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STAGE_LABELS = ["Queued", "Email 1 sent", "Email 2 sent", "Series complete"];

function statusOf(r) {
  if (r.stopped_reason === "converted") return "converted";
  if (r.stopped_reason === "replied") return "replied";
  if (r.stopped_reason === "manual_pause") return "paused";
  if (r.done) return "completed";
  return "active";
}

const STATUS_HUE = {
  active: "bg-blue-50 text-blue-700 ring-blue-200",
  completed: "bg-slate-100 text-slate-600 ring-slate-200",
  converted: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  replied: "bg-amber-50 text-amber-700 ring-amber-200",
  paused: "bg-violet-50 text-violet-700 ring-violet-200",
};

export const NurturePipeline = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/nurture").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.patch(`/admin/nurture/${id}`, { action });
      toast.success(action === "delete" ? "Removed from pipeline" : action === "pause" ? "Sequence paused" : "Sequence resumed — next email on the next hourly run");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const stats = useMemo(() => {
    const s = { active: 0, stopped: 0, converted: 0, opened: 0 };
    rows.forEach((r) => {
      const st = statusOf(r);
      if (st === "active") s.active += 1;
      else if (st === "converted") s.converted += 1;
      else s.stopped += 1;
      if ((r.opens || []).length > 0) s.opened += 1;
    });
    return s;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="nurture-pipeline">
      <div className="grid sm:grid-cols-4 gap-4">
        <KpiCard label="Active in sequence" value={stats.active} testid="nurture-kpi-active" />
        <KpiCard label="Stopped / complete" value={stats.stopped} testid="nurture-kpi-completed" />
        <KpiCard label="Converted to account" value={stats.converted} testid="nurture-kpi-converted" />
        <KpiCard label="Opened ≥1 email" value={stats.opened} testid="nurture-kpi-opened" />
      </div>

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-0">
          {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-10 text-center" data-testid="nurture-empty">
              <InboxIcon className="size-8 text-slate-300 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">No one in the nurture pipeline yet — every benchmark report download joins automatically.</p>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {rows.map((r) => {
                const st = statusOf(r);
                const stage = Math.min(3, Number(r.stage || 0));
                const opens = r.opens || [];
                return (
                  <li key={r.id} className="p-5 hover:bg-slate-50/60 transition-colors" data-testid={`nurture-row-${r.id}`}>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-900">{r.name || "—"}</span>
                          <span className={`text-[10.5px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ring-1 ${STATUS_HUE[st]}`}>
                            {st === "converted" && <Sparkles className="inline size-2.5 mr-1 -mt-0.5" />}
                            {st === "replied" && <MessageCircleReply className="inline size-2.5 mr-1 -mt-0.5" />}
                            {st}
                          </span>
                          <a href={`mailto:${r.email}`} className="text-[11px] font-mono text-slate-500 hover:text-primary">{r.email}</a>
                        </div>
                        <div className="mt-2 flex items-center gap-2.5">
                          {[0, 1, 2].map((i) => (
                            <span key={i} className={`h-1.5 w-9 rounded-full ${i < stage ? "bg-[#0055FF]" : "bg-slate-200"}`} />
                          ))}
                          <span className="text-[11px] text-slate-500 ml-1">{STAGE_LABELS[stage]}</span>
                          {opens.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium ml-2">
                              <MailOpen className="size-3" /> {opens.length} open{opens.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-[11px] font-mono text-slate-400 space-y-0.5 shrink-0">
                        {r.last_sent_at && <div>Last sent {new Date(r.last_sent_at).toLocaleDateString("en-GB")}</div>}
                        {!r.done && r.next_at && <div className="text-slate-600">Next {new Date(r.next_at).toLocaleDateString("en-GB")}</div>}
                        {st === "converted" && <div className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="size-3" /> signed up</div>}
                        {st === "replied" && <div className="text-amber-600">replied — auto-paused</div>}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {st === "active" && (
                          <Button variant="outline" size="sm" className="rounded-full h-7 px-3 text-[11px]" onClick={() => act(r.id, "pause")} data-testid={`nurture-pause-${r.id}`}>
                            <Pause className="size-3 mr-0.5" /> Pause
                          </Button>
                        )}
                        {(st === "paused" || st === "replied") && stage < 3 && (
                          <Button variant="outline" size="sm" className="rounded-full h-7 px-3 text-[11px]" onClick={() => act(r.id, "resume")} data-testid={`nurture-resume-${r.id}`}>
                            <Play className="size-3 mr-0.5" /> Resume
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="rounded-full h-7 px-3 text-[11px] text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => act(r.id, "delete")} data-testid={`nurture-delete-${r.id}`}>
                          <Trash2 className="size-3 mr-0.5" /> Remove
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
      <p className="text-[11.5px] text-slate-400">
        Downloads join the sequence automatically: day 2 break-even maths, day 5 case-study proof, day 8 funded-pilot invitation.
        The series stops the moment someone opens an account <b>or replies to any email</b> — no robotic follow-ups.
        Opens are tracked via email pixel — some clients block them, so treat counts as a floor.
      </p>
    </div>
  );
};
